/**
 * FG Stock Register · Mar 3, 2026
 *
 * Current on-hand FG per article — packed boxes minus dispatched.
 * Sourced from `GET /api/reports/fg-stock-register` which reconciles
 * `packing_slips` (Inward) against `delivery_challans` (Outward).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";

const fmt = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

export default function FGStockRegister() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/fg-stock-register");
      setRows(data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load FG register"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toUpperCase();
    if (!needle) return rows;
    return rows.filter(r => (r.product_code || "").toUpperCase().includes(needle));
  }, [rows, q]);

  const totals = useMemo(() => filtered.reduce((a, r) => ({
    on_hand:    a.on_hand    + Number(r.on_hand || 0),
    packed:     a.packed     + Number(r.packed_qty || 0),
    dispatched: a.dispatched + Number(r.dispatched_qty || 0),
    boxes:      a.boxes      + Number(r.box_count || 0),
    floor:      a.floor      + Number(r.on_hand_floor || 0),
    store:      a.store      + Number(r.on_hand_store || 0),
    reserved:   a.reserved   + Number(r.reserved_qty || 0),
    free:       a.free       + Number(r.free_to_sell || 0),
  }), { on_hand: 0, packed: 0, dispatched: 0, boxes: 0, floor: 0, store: 0, reserved: 0, free: 0 }), [filtered]);

  return (
    <div className="space-y-6" data-testid="fg-stock-register">
      <PageHeader
        overline="Sales · FG Inventory"
        title="FG Stock Register"
        subtitle="Current on-hand quantity per finished article — packed boxes minus dispatched. Sourced live from the Packing + Delivery Challan ledgers."
      />

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          ["On Hand",       fmt(totals.on_hand),    "text-emerald-300"],
          ["PACK-1FLR",     fmt(totals.floor),      "text-amber-300"],
          ["FG-STORE",      fmt(totals.store),      "text-sky-300"],
          ["Reserved (SO)", fmt(totals.reserved),   "text-purple-300"],
          ["Free-to-Sell",  fmt(totals.free),       "text-white"],
          ["Boxes",         totals.boxes,           "text-zinc-300"],
        ].map(([l, v, c]) => (
          <div key={l} className="border border-[var(--border)] bg-[var(--bg-1)] rounded-sm p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{l}</div>
            <div className={`text-2xl font-mono tabular-nums mt-1 ${c}`}>{v}</div>
          </div>
        ))}
      </div>

      <input
        data-testid="fgsr-search"
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder="Search product code…"
        className="h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm font-mono text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[var(--accent)] w-72"
      />

      <div className="border border-[var(--border)] rounded-sm overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-2)]/30 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left px-3 py-2">Product Code</th>
              <th className="text-right px-3 py-2 w-28" title="Packed at PACK-1FLR — not yet moved to FG-Store">PACK-1FLR</th>
              <th className="text-right px-3 py-2 w-28" title="Moved to FG-Store (via a Packing List)">FG-STORE</th>
              <th className="text-right px-3 py-2 w-28">On Hand</th>
              <th className="text-right px-3 py-2 w-28" title="Pending Sales Order commitments — order_qty − dispatched − cancelled">Reserved</th>
              <th className="text-right px-3 py-2 w-28" title="Free-to-Sell = On Hand − Reserved">Free-to-Sell</th>
              <th className="text-right px-3 py-2 w-20">Boxes</th>
              <th className="text-left px-3 py-2 w-32">Last Pack</th>
              <th className="text-left px-3 py-2 w-14">UoM</th>
              <th className="text-left px-3 py-2 w-24">Stock Book</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="px-3 py-6 text-center text-zinc-500 text-xs font-mono uppercase tracking-widest">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={10} className="px-3 py-8 text-center text-zinc-500 text-xs">No FG on hand</td></tr>}
            {!loading && filtered.map((r) => {
              const oversold = Number(r.reserved_qty || 0) > Number(r.on_hand || 0);
              return (
                <tr key={r.product_code} data-testid={`fgsr-row-${r.product_code}`} className="border-t border-[var(--border)]/60 hover:bg-[var(--bg-2)]/20">
                  <td className="px-3 py-2 font-mono text-[var(--accent)]">{r.product_code}</td>
                  <td className={`px-3 py-2 text-right font-mono tabular-nums ${r.on_hand_floor > 0 ? "text-amber-300" : "text-zinc-500"}`} data-testid={`fgsr-floor-${r.product_code}`} title={`${r.boxes_floor || 0} box(es)`}>
                    {fmt(r.on_hand_floor)}
                    <span className="text-[9px] text-zinc-500 ml-1">·{r.boxes_floor || 0}b</span>
                  </td>
                  <td className={`px-3 py-2 text-right font-mono tabular-nums ${r.on_hand_store > 0 ? "text-sky-300" : "text-zinc-500"}`} data-testid={`fgsr-store-${r.product_code}`} title={`${r.boxes_store || 0} box(es)`}>
                    {fmt(r.on_hand_store)}
                    <span className="text-[9px] text-zinc-500 ml-1">·{r.boxes_store || 0}b</span>
                  </td>
                  <td className={`px-3 py-2 text-right font-mono tabular-nums font-semibold ${r.on_hand > 0 ? "text-emerald-300" : "text-zinc-500"}`}>{fmt(r.on_hand)}</td>
                  <td className={`px-3 py-2 text-right font-mono tabular-nums ${r.reserved_qty > 0 ? (oversold ? "text-red-300 font-semibold" : "text-purple-300") : "text-zinc-500"}`} data-testid={`fgsr-reserved-${r.product_code}`}>
                    {fmt(r.reserved_qty)}
                    {oversold && <span className="ml-1 text-[9px] font-mono uppercase tracking-widest px-1 py-0.5 rounded-sm border border-red-500/40 bg-red-500/10 text-red-300">SHORT</span>}
                  </td>
                  <td className={`px-3 py-2 text-right font-mono tabular-nums ${r.free_to_sell > 0 ? "text-white" : "text-red-400"}`} data-testid={`fgsr-free-${r.product_code}`}>{fmt(r.free_to_sell)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300">{r.box_count}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-zinc-400">{r.last_pack_date || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-500">{r.uom}</td>
                  <td className="px-3 py-2">
                    <Link
                      to={`/sales/fg-stock-book?article=${encodeURIComponent(r.product_code)}`}
                      className="text-[10px] font-mono uppercase tracking-widest text-[var(--accent)] hover:underline"
                    >Open →</Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
